"use client";

import { useState, useCallback } from "react";
import React from "react";
import { ResponsiveModal } from "@wtc/ui";
import { Button } from "@/components/ui/button";

interface ConfirmationDialogOptions {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
}

interface UseConfirmationDialogReturn {
  confirm: (onConfirm: () => void) => void;
  ConfirmationDialog: React.FC;
}

export function useConfirmationDialog({
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
}: ConfirmationDialogOptions): UseConfirmationDialogReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);

  const confirm = useCallback((confirmAction: () => void) => {
    setOnConfirm(() => confirmAction);
    setIsOpen(true);
  }, []);

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const ConfirmationDialog: React.FC = () =>
    React.createElement(
      ResponsiveModal as any,
      {
        open: isOpen,
        onOpenChange: setIsOpen,
        title,
        description,
        maxWidth: "md",
        side: "bottom",
      },
      React.createElement(
        "div",
        { className: "flex gap-3 pt-4" },
        React.createElement(
          Button,
          {
            variant: "outline",
            onClick: handleCancel,
            className: "flex-1",
          },
          cancelText
        ),
        React.createElement(
          Button,
          {
            onClick: handleConfirm,
            className: "flex-1",
          },
          confirmText
        )
      )
    );

  return { confirm, ConfirmationDialog };
}
